import type { ResolvedAsset as Asset } from "@contentful-api/cmsResolvedTypes";
import type { IMediaAsset } from "@contentful-api/types/contentful";

export interface IResolvedMediaAsset {
    url: string;
    title: string;
    description: string;
    alt: string;
    width?: number;
    height?: number;
}

/**
 * Resolves the url/title/description/dimensions to render for a Contentful field pair where an
 * Aprimo-backed `IMediaAsset` field (eg. `desktopAsset`) sits alongside a legacy plain Contentful
 * `Asset` field (eg. `imageDesktop`). The Aprimo asset wins when it is set, the legacy asset is the
 * fallback for content that has not been migrated yet.
 *
 * This is the only place that should read `.fields.file` off an `Asset`/`IMediaAsset` directly -
 * everywhere else should go through this helper so the Aprimo sibling is never missed.
 */
export const resolveMediaAsset = (
    asset?: IMediaAsset,
    image?: Asset
): IResolvedMediaAsset | undefined => {
    const assetFile = asset?.fields?.file;
    const imageFile = image?.fields?.file;
    const url = assetFile?.publicUri ?? assetFile?.url ?? imageFile?.url;

    if (!url) return undefined;

    const title = asset?.fields?.title || image?.fields?.title || "";
    const description = asset?.fields?.description || image?.fields?.description || "";
    const dimensions = assetFile?.details?.image ?? imageFile?.details?.image;

    return {
        url,
        title,
        description,
        alt: description || title,
        width: dimensions?.width,
        height: dimensions?.height,
    };
};
